import React, { useMemo } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const AttributeSumBarChartSettings = ({
  title,
  attributeSource,
  objectTypes,
  eventTypes,
  objectTypeNumericAttributes,
  eventTypeNumericAttributes,
  selectedObjectTypes,
  selectedObjectAttributes,
  selectedEventTypes,
  selectedEventAttributes,
  enabledGranularities,
  onTitleChange,
  onAttributeSourceChange,
  onSelectedObjectTypesChange,
  onSelectedObjectAttributesChange,
  onSelectedEventTypesChange,
  onSelectedEventAttributesChange,
  onEnabledGranularitiesChange,
  dateRangeStart,
  dateRangeEnd,
  onDateRangePresetChange,
  loading = false,
}) => {
  const toggleGranularity = (id) => {
    if (enabledGranularities.includes(id)) {
      const next = enabledGranularities.filter((g) => g !== id);
      if (next.length > 0) {
        onEnabledGranularitiesChange(next);
      }
      return;
    }
    onEnabledGranularitiesChange([...enabledGranularities, id]);
  };

  // Compute available numeric attributes based on selected types (union)
  const availableObjectAttributes = useMemo(() => {
    if (!selectedObjectTypes.length) return [];
    const attrSet = new Set();
    selectedObjectTypes.forEach((ot) => {
      const attrs = objectTypeNumericAttributes[ot] || [];
      attrs.forEach((a) => attrSet.add(a));
    });
    return Array.from(attrSet).sort();
  }, [selectedObjectTypes, objectTypeNumericAttributes]);

  const availableEventAttributes = useMemo(() => {
    if (!selectedEventTypes.length) return [];
    const attrSet = new Set();
    selectedEventTypes.forEach((et) => {
      const attrs = eventTypeNumericAttributes[et] || [];
      attrs.forEach((a) => attrSet.add(a));
    });
    return Array.from(attrSet).sort();
  }, [selectedEventTypes, eventTypeNumericAttributes]);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Attribute Sum Bar Chart"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Attribute Source
        </label>
        <div className="flex gap-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="attributeSource"
              value="object"
              checked={attributeSource === "object"}
              onChange={() => onAttributeSourceChange("object")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">Object Attribute</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="attributeSource"
              value="event"
              checked={attributeSource === "event"}
              onChange={() => onAttributeSourceChange("event")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">Event Attribute</span>
          </label>
        </div>
      </div>

      {attributeSource === "object" && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Object Configuration
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <MultiSelectDropdown
                label="Object Type *"
                options={(objectTypes || []).map(String)}
                selectedValues={selectedObjectTypes}
                onSelectionChange={onSelectedObjectTypesChange}
                placeholder="Select object types..."
                disabled={loading || objectTypes.length === 0}
                itemType="object types"
              />
            </div>
            <div>
              <MultiSelectDropdown
                label="Object Attribute *"
                options={availableObjectAttributes.map(String)}
                selectedValues={selectedObjectAttributes}
                onSelectionChange={onSelectedObjectAttributesChange}
                placeholder={
                  selectedObjectTypes.length === 0
                    ? "Select object types first..."
                    : availableObjectAttributes.length === 0
                      ? "No numeric attributes available"
                      : "Select attributes..."
                }
                disabled={loading || availableObjectAttributes.length === 0}
                itemType="attributes"
              />
            </div>
          </div>
        </div>
      )}

      {attributeSource === "event" && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Event Configuration
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <MultiSelectDropdown
                label="Event Type *"
                options={(eventTypes || []).map(String)}
                selectedValues={selectedEventTypes}
                onSelectionChange={onSelectedEventTypesChange}
                placeholder="Select event types..."
                disabled={loading || eventTypes.length === 0}
                itemType="event types"
              />
            </div>
            <div>
              <MultiSelectDropdown
                label="Event Attribute *"
                options={availableEventAttributes.map(String)}
                selectedValues={selectedEventAttributes}
                onSelectionChange={onSelectedEventAttributesChange}
                placeholder={
                  selectedEventTypes.length === 0
                    ? "Select event types first..."
                    : availableEventAttributes.length === 0
                      ? "No numeric attributes available"
                      : "Select attributes..."
                }
                disabled={loading || availableEventAttributes.length === 0}
                itemType="attributes"
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Enabled Granularities (View Mode)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {GRANULARITY_OPTIONS.map((option) => {
            const active = enabledGranularities.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGranularity(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {onDateRangePresetChange && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Date Range (Date Range Mode)
          </label>
          <DateRangePresets
            value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
            onChange={onDateRangePresetChange}
          />
        </div>
      )}
    </div>
  );
};

export default AttributeSumBarChartSettings;
